import { useState, useCallback, useMemo, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { TicketType } from "../types/EventPaymentTypes";

export const FLIP_ANIMATION_DELAY = 280;
export const FLIP_ANIMATION_DURATION = 600;

export type ViewType =
  | "tickets"
  | "auth"
  | "email-auth"
  | "purchase"
  | "my-tickets";

export type TicketStatus =
  | "available"
  | "sold_out"
  | "coming_soon"
  | "sale_ended";

export interface TicketTypeWithId extends TicketType {
  ticket_id: string;
  quantity_sold: number;
  status: TicketStatus;
}

export interface AdditionalAttendee {
  first_name: string;
  last_name: string;
  user_email: string;
}

export interface AttendeeFormData {
  attendees: AdditionalAttendee[];
}

export interface MyTicket {
  ticket_id: string;
  timestamp: string;
  status: "active" | "used";
  [key: string]: unknown;
}

export interface PaymentState {
  isCalculated: boolean;
  paymentLink: string | null;
  calculatedAmount: number | null;
  effectiveQuantityForPayment: number | null;
  purchaserAlreadyHasTicket: boolean;
  attendeeAlreadyHasTicket: Record<string, boolean>;
}

export type PurchaseStep = "form" | "validated" | "reserved";

export interface ValidationResult {
  can_proceed: boolean;
  purchaser_already_has_ticket: boolean;
  purchaser_email: string | null;
  effective_quantity: number;
  valid_attendees: Array<{
    first_name: string;
    last_name: string;
    user_email: string;
    user_identification?: string;
  }>;
  price_in_cents: number;
  price_per_ticket: number;
  currency: string;
  warnings?: Record<string, Record<string, string>>;
  info?: Record<string, Record<string, string>>;
}

export interface ReservationResult {
  reservation_id: string;
  expires_at: string;
  quantity_reserved: number;
  price_in_cents: number;
  currency?: string;
  time_remaining_seconds: number;
  existing_reservation?: boolean;
}

export const buildAttendeeFormSchema = (t: (key: string) => string) => {
  const attendeeSchema = Joi.object({
    first_name: Joi.string()
      .min(2)
      .max(30)
      .required()
      .messages({
        "string.empty": t("event.purchase.firstNameRequired"),
        "string.min": t("event.purchase.firstNameMinLength"),
        "string.max": t("event.purchase.firstNameMaxLength"),
        "any.required": t("event.purchase.firstNameRequired"),
      }),
    last_name: Joi.string()
      .min(2)
      .max(30)
      .required()
      .messages({
        "string.empty": t("event.purchase.lastNameRequired"),
        "string.min": t("event.purchase.lastNameMinLength"),
        "string.max": t("event.purchase.lastNameMaxLength"),
        "any.required": t("event.purchase.lastNameRequired"),
      }),
    user_email: Joi.string()
      .email({ tlds: { allow: false } })
      .max(200)
      .required()
      .messages({
        "string.empty": t("event.purchase.emailRequired"),
        "string.email": t("event.errors.validEmails"),
        "string.max": t("event.purchase.emailMaxLength"),
        "any.required": t("event.purchase.emailRequired"),
      }),
  });

  return Joi.object({
    attendees: Joi.array().items(attendeeSchema),
  });
};

interface UseEventCardStateProps {
  availableTickets: TicketTypeWithId[];
  t: (key: string) => string;
}

export function useEventCardState({
  availableTickets,
  t,
}: UseEventCardStateProps) {
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [ticketQuantity, setTicketQuantity] = useState<number>(1);

  const [showBackFace, setShowBackFace] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>("tickets");
  const [isFlipping, setIsFlipping] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);

  const [myTickets, setMyTickets] = useState<MyTicket[]>([]);

  const [expandedAttendees, setExpandedAttendees] = useState<Set<number>>(
    new Set()
  );

  const [purchaseStep, setPurchaseStep] = useState<PurchaseStep>("form");
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [reservationResult, setReservationResult] =
    useState<ReservationResult | null>(null);

  const [paymentState, setPaymentState] = useState<PaymentState>({
    isCalculated: false,
    paymentLink: null,
    calculatedAmount: null,
    effectiveQuantityForPayment: null,
    purchaserAlreadyHasTicket: false,
    attendeeAlreadyHasTicket: {},
  });

  const attendeeFormSchema = useMemo(() => buildAttendeeFormSchema(t), [t]);

  const form = useForm<AttendeeFormData>({
    resolver: joiResolver(attendeeFormSchema),
    defaultValues: { attendees: [] },
    mode: "onChange",
  });

  const fieldArray = useFieldArray({
    control: form.control,
    name: "attendees",
  });

  const watchedAttendees = form.watch("attendees");

  useEffect(() => {
    const additionalCount = ticketQuantity - 1;
    const currentCount = fieldArray.fields.length;

    if (additionalCount > currentCount) {
      for (let i = currentCount; i < additionalCount; i++) {
        fieldArray.append({ first_name: "", last_name: "", user_email: "" });
      }
    } else if (additionalCount < currentCount) {
      for (let i = currentCount - 1; i >= additionalCount; i--) {
        fieldArray.remove(i);
      }
    }
  }, [
    ticketQuantity,
    fieldArray.fields.length,
    fieldArray.append,
    fieldArray.remove,
  ]);

  const selectedTicket = useMemo(
    () => availableTickets.find((t) => t.ticket_id === selectedTicketId),
    [availableTickets, selectedTicketId]
  );

  const totalAmount = selectedTicket
    ? selectedTicket.settings.price * ticketQuantity
    : 0;

  const flipToBack = useCallback(
    (view: Exclude<ViewType, "tickets">) => {
      if (isFlipping) return;
      setIsFlipping(true);
      window.setTimeout(() => {
        setShowBackFace(true);
        setCurrentView(view);
      }, FLIP_ANIMATION_DELAY);
      window.setTimeout(() => setIsFlipping(false), FLIP_ANIMATION_DURATION);
    },
    [isFlipping]
  );

  const flipToFront = useCallback(() => {
    if (isFlipping) return;
    setIsFlipping(true);
    window.setTimeout(() => {
      setShowBackFace(false);
      setCurrentView("tickets");
      resetPaymentState();
    }, FLIP_ANIMATION_DELAY);
    window.setTimeout(() => setIsFlipping(false), FLIP_ANIMATION_DURATION);
  }, [isFlipping]);

  const flipToView = useCallback(
    (view: ViewType) => {
      if (isFlipping) return;
      setIsFlipping(true);
      window.setTimeout(() => {
        setCurrentView(view);
      }, FLIP_ANIMATION_DELAY);
      window.setTimeout(() => setIsFlipping(false), FLIP_ANIMATION_DURATION);
    },
    [isFlipping]
  );

  const selectTicket = useCallback(
    (ticketId: string) => {
      if (selectedTicketId === ticketId) {
        setSelectedTicketId(null);
        setTicketQuantity(1);
        form.reset({ attendees: [] });
      } else {
        const ticket = availableTickets.find((t) => t.ticket_id === ticketId);
        if (ticket && ticket.status === "available") {
          setSelectedTicketId(ticketId);
          const minQty = ticket.settings.min_per_order || 1;
          setTicketQuantity(minQty);
          form.reset({
            attendees: Array.from({ length: minQty - 1 }, () => ({
              first_name: "",
              last_name: "",
              user_email: "",
            })),
          });
        }
      }
    },
    [availableTickets, selectedTicketId, form]
  );

  const toggleAttendeeExpanded = useCallback((index: number) => {
    setExpandedAttendees((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const expandAttendees = useCallback((indexes: number[]) => {
    setExpandedAttendees((prev) => {
      const next = new Set(prev);
      indexes.forEach((index) => next.add(index));
      return next;
    });
  }, []);

  const resetPaymentState = useCallback(() => {
    setPurchaseStep("form");
    setValidationResult(null);
    setReservationResult(null);
    setPaymentState({
      isCalculated: false,
      paymentLink: null,
      calculatedAmount: null,
      effectiveQuantityForPayment: null,
      purchaserAlreadyHasTicket: false,
      attendeeAlreadyHasTicket: {},
    });
  }, []);

  const updatePaymentState = useCallback((updates: Partial<PaymentState>) => {
    setPaymentState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetAll = useCallback(() => {
    setSelectedTicketId(null);
    setTicketQuantity(1);
    form.reset({ attendees: [] });
    resetPaymentState();
  }, [form, resetPaymentState]);

  return {
    selectedTicketId,
    selectedTicket,
    ticketQuantity,
    setTicketQuantity,
    selectTicket,
    totalAmount,

    showBackFace,
    setShowBackFace,
    currentView,
    isFlipping,
    flipToBack,
    flipToFront,
    flipToView,

    isProcessing,
    setIsProcessing,
    isLoadingTickets,
    setIsLoadingTickets,

    myTickets,
    setMyTickets,

    expandedAttendees,
    toggleAttendeeExpanded,
    expandAttendees,

    purchaseStep,
    setPurchaseStep,
    validationResult,
    setValidationResult,
    reservationResult,
    setReservationResult,

    paymentState,
    updatePaymentState,
    resetPaymentState,

    form,
    fieldArray,
    watchedAttendees,

    resetAll,
  };
}
